// /developers: the Bumpups API for people deciding whether to build on it. The reference itself is /developers/docs.
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../AuthContext';
import CtaBand from '../site/CtaBand';
import Faq from '../site/Faq';
import Seo, { ORGANIZATION, SITE_URL } from '../site/Seo';
import { Code } from '../../myApi/ApiReference';
import { LIMITS, SAMPLE_ANSWER, SAMPLE_CHAPTERS } from '../../myApi/apiDocsContent';
import { PRICE_PER_BLOCK } from '../../myPlans/planPricing';
import ApiConsole from './ApiConsole';
import './developers.css';

const STEPS = [
  ['Create a key', 'On Pro, open the API page in the app and create a key. It’s shown once, so store it on your server.', 'Authorization: Bearer bu_live_…'],
  ['Send a YouTube link', 'The video is accepted right away and processed in the background. It costs its length in minutes.', 'POST /v1/videos  {"url": "…"}'],
  ['Get chapters, then ask', 'Check back until the status is ready. Use the chapters, or ask questions about the video.', 'GET /v1/videos/{id}'],
];

const STANDARDS = [
  ['REST and JSON', 'Versioned paths, predictable resources, snake_case fields and RFC 3339 times.', 'overview'],
  ['OpenAPI 3.1', 'A machine-readable description of every endpoint, for code generators and AI agents.', 'overview'],
  ['Problem details', 'Errors in the RFC 9457 format, with a stable code and a request id.', 'errors'],
  ['Safe retries', 'Send an Idempotency-Key and a retried request never charges twice.', 'create-video'],
  ['Async by design', 'Long work answers 202 with a Location to check and a Retry-After hint.', 'get-video'],
  ['Keys you control', 'Shown once, stored only as a hash, revoked from the app in a minute.', 'authentication'],
];

const FAQ = [
  ['Who can use the Bumpups API?', `Pro accounts. Pro starts at $${PRICE_PER_BLOCK.monthly} a month for 1,000 minutes, and the API uses the same minutes as the web app.`],
  ['Which videos does it work with?', 'Public YouTube videos up to 3 hours long, and unlisted ones of 30 minutes or more. Live streams and premieres aren’t supported yet. Uploads stay in the web app.'],
  ['How long does a video take?', 'Videos under 30 minutes are usually ready in 1–2 minutes; longer ones take 5–15 minutes. The API answers right away and you check back.'],
  ['Do questions cost minutes?', 'No. Each video allows 25 questions, counted together with chat in the web app, and they don’t use minutes.'],
  ['Do API videos show up in my account?', 'Yes. Every video you process through the API appears in your Workspace with an API tag, where you can open it, chat about it and export it.'],
  ['Can I call it from a website?', 'No. Keys are secrets, so call the API from your server. Browsers can’t call it directly.'],
];

const chaptersJson = JSON.stringify(SAMPLE_CHAPTERS.slice(0, 3), null, 2);

export default function DevelopersPage() {
  const { currentUser } = useContext(AuthContext);
  const keysTo = currentUser ? '/api' : '/signup';

  return (
    <>
      <Seo
        title="YouTube Chapters & Video Q&A API | Bumpups"
        description="Turn any YouTube link into timestamped chapters and ask questions about the video from your own code. A REST API with an OpenAPI description, included with Bumpups Pro."
        jsonLd={[{
          '@type': 'WebAPI',
          name: 'Bumpups API',
          description: 'Timestamped chapters and answers about YouTube videos, from your own code.',
          documentation: `${SITE_URL}/developers/docs`,
          url: `${SITE_URL}/developers`,
          provider: ORGANIZATION,
        }]}
      />

      <section className="dev-hero mk-hero-bg">
        <div className="mk-container dev-hero-grid">
          <div className="dev-hero-text">
            <h1 className="mk-h1 dev-title">Chapters and answers for YouTube videos, from your own code</h1>
            <p className="mk-lead">
              Send a YouTube link to the Bumpups API and get back timestamped chapters, ready to paste into a description.
              Then ask questions about the video. It comes with Pro and uses the same minutes as the app.
            </p>
            <div className="mk-actions">
              <Link to={keysTo} className="mk-btn mk-btn--primary">
                Get your API key <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
              </Link>
              <Link to="/developers/docs" className="mk-btn">Read the docs</Link>
            </div>
            <p className="mk-note">Included with Pro. The output shown is real: a Harvard lecture processed by the API.</p>
          </div>
          <ApiConsole />
        </div>
      </section>

      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <h2 className="mk-h2">Three calls from link to chapters</h2>
          </div>
          <ol className="dev-steps">
            {STEPS.map(([title, text, code], i) => (
              <li key={title} className="mk-card dev-step">
                <span className="dev-step-number">{i + 1}</span>
                <h3 className="mk-h3">{title}</h3>
                <p className="mk-text">{text}</p>
                <code className="dev-step-code">{code}</code>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head">
            <h2 className="mk-h2">What comes back</h2>
            <p className="mk-lead">The same chapters and answers you get in the app, as JSON.</p>
          </div>
          <div className="dev-outputs">
            <div className="dev-output">
              <h3 className="mk-h3">Chapters</h3>
              <p className="mk-text">A list with start times in seconds, plus <code>chapters_text</code> for a YouTube description.</p>
              <div className="apid-panel dev-panel"><Code code={chaptersJson} lang="json" /></div>
            </div>
            <div className="dev-output">
              <h3 className="mk-h3">Answers</h3>
              <p className="mk-text">Ask anything about the video and get Markdown back. Follow-up questions keep the context.</p>
              <div className="dev-chat">
                <p className="dev-chat-question">Which cases does the professor use to test our moral intuitions?</p>
                <div className="dev-chat-answer"><ReactMarkdown>{SAMPLE_ANSWER}</ReactMarkdown></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container dev-split">
          <div>
            <h2 className="mk-h2">One account, shared limits</h2>
            <p className="mk-lead">
              Pro is ${PRICE_PER_BLOCK.monthly} a month for 1,000 minutes. The API and the web app draw on the same
              balance, and every API video appears in your Workspace.
            </p>
            <div className="mk-actions">
              <Link to="/pricing" className="mk-btn">See Pro plans</Link>
            </div>
          </div>
          <dl className="dev-limits">
            {LIMITS.map(([name, text]) => (
              <div key={name} className="dev-limit"><dt>{name}</dt><dd>{text}</dd></div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head">
            <h2 className="mk-h2">Built the way developers expect</h2>
          </div>
          <div className="mk-grid mk-grid--3">
            {STANDARDS.map(([title, text, anchor]) => (
              <Link key={title} to={`/developers/docs#${anchor}`} className="mk-card dev-standard">
                <h3 className="mk-h3">{title}</h3>
                <p className="mk-text">{text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Faq title="API questions" items={FAQ} />

      <CtaBand title="Build with the Bumpups API" text={`Included with Pro: $${PRICE_PER_BLOCK.monthly} a month for 1,000 minutes.`}>
        <Link to={keysTo} className="mk-btn mk-btn--primary">
          Get your API key <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
        </Link>
        <Link to="/developers/docs" className="mk-btn">Read the docs</Link>
      </CtaBand>
    </>
  );
}
